import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from './Icons';

const MAX_LIST_HEIGHT = 260;
// The list is allowed to grow wider than a narrow trigger (e.g. the compact
// per-row agent picker) so option labels aren't cut off — capped so it can't
// balloon past a sane width, and never wider than the option text needs.
const MAX_LIST_WIDTH = 260;
const GAP = 6;

// Custom dropdown replacing a native <select> — browsers render a native
// select's option list with OS-controlled colors (that flat white-and-blue
// list), which can't be themed. This renders its own popover list instead,
// so it picks up the app's light/dark palette and hover/selected states.
//
// The list is portaled to document.body rather than rendered inline: some
// callers (e.g. the per-row agent picker in a scrollable data table) live
// inside an ancestor with overflow-x: auto, which per spec forces
// overflow-y to clip too — an inline absolutely-positioned list would get
// cut off on rows near the bottom. Portaling escapes that, positioned from
// the trigger's live bounding rect and flipped above it when there isn't
// room below.
function SelectMenu({ id, value, options, onChange, ariaLabel, className = '', disabled = false, icon = null }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // Scroll position (of the page or a scrollable ancestor like the table
    // container) would leave a fixed-position menu detached from its
    // trigger, so close rather than let it drift. `true` catches scrolls on
    // any inner scroll container, not just the window. But scroll events
    // don't bubble, so a capture-phase window listener also fires when the
    // menu's own option list (itself scrollable when there are many
    // options) is scrolled - without this check, that scroll would
    // immediately close the menu instead of letting it scroll.
    const handleScroll = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  const handleTriggerClick = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    // Clamped so a list that grows wider than the trigger still can't run
    // off the right edge of the viewport.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - MAX_LIST_WIDTH - 8));
    setMenuStyle({
      position: 'fixed',
      left,
      minWidth: rect.width,
      maxWidth: MAX_LIST_WIDTH,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.min(MAX_LIST_HEIGHT, spaceAbove - 12) }
        : { top: rect.bottom + GAP, maxHeight: Math.min(MAX_LIST_HEIGHT, spaceBelow - 12) }),
    });
    setOpen(true);
  };

  const selected = options.find((option) => option.value === value);

  return (
    <div className={`select-menu ${className}`} ref={containerRef}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={`select-menu-trigger ${open ? 'open' : ''}`}
        onClick={handleTriggerClick}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className="select-menu-trigger-label">
          {icon && <span className="select-menu-leading-icon">{icon}</span>}
          <span>{selected ? selected.label : ''}</span>
        </span>
        <ChevronDownIcon className="select-caret" width={14} height={14} />
      </button>
      {open &&
        menuStyle &&
        createPortal(
          <ul ref={menuRef} className="select-menu-list" role="listbox" aria-label={ariaLabel} style={menuStyle}>
            {options.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={`select-menu-option ${option.value === value ? 'selected' : ''}`}
                  style={option.color && option.value !== value ? { color: option.color } : undefined}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
}

export default SelectMenu;
